import React, { useCallback, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Upload, Sparkles, RotateCcw, Trash2, Image as ImageIcon, User as UserIcon } from "lucide-react";
import {
  SKIN_TONES, HAIR_STYLES, HAIR_COLORS, HAIR_HIDDEN_TOPS, EYEBROWS, EYES, MOUTHS, ACCESSORIES,
  CLOTHING, CLOTHING_COLORS, BACKGROUND_COLORS, EXPRESSIONS,
  FACIAL_HAIRS, FACIAL_HAIR_COLORS,
  DEFAULT_AVATAR_CONFIG, illustratedUrl, randomAvatarConfig, resolveAvatarUrl,
} from "@/lib/avatar";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];

/**
 * Personal avatar setup — two tabs, photo upload and DiceBear avataaars editor.
 * Every visible control is guaranteed to change the live preview.
 */
export default function AvatarSetup({ onSaved, onCancel, embedded = false }) {
  const { user } = useAuth();
  const [tab, setTab] = useState(user?.avatar_type === "photo" ? "photo" : "illustrated");

  return (
    <div className={embedded ? "" : "space-y-4"} data-testid="avatar-setup">
      <div className="flex gap-2">
        <button onClick={() => setTab("photo")}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${tab === "photo" ? "bg-[var(--brand-primary)] text-white" : "bg-zinc-100 hover:bg-zinc-200"}`}
          data-testid="avatar-tab-photo">
          <ImageIcon className="w-4 h-4 inline mr-1" /> Upload photo
        </button>
        <button onClick={() => setTab("illustrated")}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${tab === "illustrated" ? "bg-[var(--brand-primary)] text-white" : "bg-zinc-100 hover:bg-zinc-200"}`}
          data-testid="avatar-tab-illustrated">
          <UserIcon className="w-4 h-4 inline mr-1" /> Illustrated avatar
        </button>
      </div>
      {tab === "photo" ? (
        <PhotoUploader onSaved={onSaved} onCancel={onCancel}/>
      ) : (
        <IllustratedEditor onSaved={onSaved} onCancel={onCancel}/>
      )}
    </div>
  );
}

/* ---------------------------- Photo ---------------------------- */

function PhotoUploader({ onSaved, onCancel }) {
  const { user, refresh } = useAuth();
  const inputRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = (file) => {
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) { toast.error("Use JPG, PNG or WEBP"); return; }
    if (file.size > MAX_BYTES) { toast.error("Image is too large (max 5 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setImgSrc(reader.result);
    reader.readAsDataURL(file);
  };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f) onFile(f); };
  const onCropComplete = useCallback((_area, pixels) => setCroppedArea(pixels), []);

  const save = async () => {
    if (!imgSrc || !croppedArea) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(imgSrc, croppedArea);
      const fd = new FormData();
      fd.append("file", blob, "avatar.png");
      const { data } = await api.post("/me/avatar/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh?.();
      toast.success("Profile photo updated");
      onSaved?.({ avatar_type: "photo", profile_photo_url: data.url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove your uploaded photo? Your illustrated avatar will be used instead.")) return;
    try {
      await api.delete("/me/avatar/photo");
      await refresh?.();
      toast.success("Photo removed");
      onSaved?.({ avatar_type: "illustrated" });
    } catch { toast.error("Failed to remove"); }
  };

  const hasPhoto = user?.avatar_type === "photo" && (user?.profile_photo_url || user?.avatar_url);

  return (
    <div className="space-y-4">
      {!imgSrc ? (
        <div className="space-y-3">
          <div onDrop={onDrop} onDragOver={(e) => e.preventDefault()} onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center cursor-pointer hover:border-[var(--brand-accent)] hover:bg-orange-50 transition"
            data-testid="avatar-upload-drop">
            <Upload className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
            <div className="text-sm font-medium">Drop image or click to browse</div>
            <div className="text-xs text-zinc-500 mt-1">JPG, PNG, or WEBP · up to 5 MB</div>
          </div>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])} data-testid="avatar-upload-input"/>
          {hasPhoto && (
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div className="flex items-center gap-3">
                <img src={resolveAvatarUrl(user)} className="w-10 h-10 rounded-full object-cover" alt="" />
                <div className="text-sm">
                  <div className="font-medium">Current photo</div>
                  <div className="text-xs text-zinc-500">Uploaded</div>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={removePhoto} data-testid="avatar-remove-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative bg-zinc-900 rounded-lg overflow-hidden" style={{ height: 260 }}>
            <Cropper image={imgSrc} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete}/>
          </div>
          <div>
            <label className="text-xs uppercase text-zinc-500">Zoom</label>
            <Slider min={1} max={3} step={0.05} value={[zoom]} onValueChange={(v) => setZoom(v[0])} data-testid="avatar-zoom" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setImgSrc(null); setCroppedArea(null); }} data-testid="avatar-replace-btn">Replace</Button>
            <Button variant="outline" onClick={onCancel || (() => { setImgSrc(null); setCroppedArea(null); })} data-testid="avatar-cancel-btn">Cancel</Button>
            <Button className="ml-auto bg-[var(--brand-primary)] text-white" onClick={save} disabled={busy || !croppedArea} data-testid="avatar-save-photo">
              {busy ? "Saving…" : "Save photo"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

async function cropToBlob(src, area) {
  const img = await new Promise((res, rej) => {
    const el = new Image();
    el.crossOrigin = "anonymous";
    el.onload = () => res(el);
    el.onerror = rej;
    el.src = src;
  });
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return await new Promise((res) => canvas.toBlob(res, "image/png", 0.92));
}

/* ---------------------------- Illustrated ---------------------------- */

function IllustratedEditor({ onSaved, onCancel }) {
  const { user, refresh } = useAuth();
  // Filter incoming legacy config: old avatars stored keys like `hair`, `style`,
  // `variant01` from the previous notionists-based editor. Those don't work
  // with avataaars, so we ignore them and start from DEFAULT_AVATAR_CONFIG.
  const LEGACY = new Set(["style", "hair"]);
  const VALID_HAIR = new Set(HAIR_STYLES.map((h) => h.value));
  const VALID_EYEBROWS = new Set(EYEBROWS.map((e) => e.value));
  const VALID_EYES = new Set(EYES.map((e) => e.value));
  const VALID_MOUTH = new Set(MOUTHS.map((m) => m.value));
  const VALID_ACC = new Set(ACCESSORIES.map((a) => a.value));
  const VALID_CLOTH = new Set(CLOTHING.map((c) => c.value));
  const VALID_FACIAL_HAIR = new Set(FACIAL_HAIRS.map((f) => f.value));

  const [config, setConfig] = useState(() => {
    const cur = user?.avatar_config || {};
    const cleaned = { ...DEFAULT_AVATAR_CONFIG };
    for (const [k, v] of Object.entries(cur)) {
      if (LEGACY.has(k)) continue;
      if (k === "top" && !VALID_HAIR.has(v)) continue;
      if (k === "eyebrows" && !VALID_EYEBROWS.has(v)) continue;
      if (k === "eyes" && !VALID_EYES.has(v)) continue;
      if (k === "mouth" && !VALID_MOUTH.has(v)) continue;
      if (k === "accessories" && !VALID_ACC.has(v) && v !== "none") continue;
      if (k === "clothes") { /* legacy key: drop, use `clothing` */ continue; }
      if (k === "clothing" && !VALID_CLOTH.has(v)) continue;
      // Facial hair — patch §5A: treat missing/invalid as None. NEVER randomly
      // assign facial hair to a user who never explicitly saved one.
      if (k === "facialHair" && !VALID_FACIAL_HAIR.has(v)) continue;
      cleaned[k] = v;
    }
    // Legacy users who saved BEFORE the independent facial-hair control existed
    // will have no `facialHair` key at all → keep default "none" (clean-shaven).
    if (!cleaned.facialHair) cleaned.facialHair = "none";
    // Once the user picks a head covering that hides hair, hair color is
    // hidden in the UI — but keep any previously chosen value intact.
    return cleaned;
  });
  const [seed, setSeed] = useState(user?.avatar_seed || user?.email || user?.id || "u");
  const [expression, setExpression] = useState("neutral");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  // Applying an expression overrides eyes+mouth for the preview and on save.
  const effectiveConfig = useMemo(() => {
    if (expression === "neutral") return config;
    const preset = EXPRESSIONS[expression] || {};
    return { ...config, ...preset };
  }, [config, expression]);

  const previewUser = useMemo(() => ({
    ...user, avatar_seed: seed, avatar_config: effectiveConfig,
  }), [user, seed, effectiveConfig]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/me/avatar/config", { avatar_seed: seed, avatar_config: effectiveConfig });
      await refresh?.();
      toast.success("Illustrated avatar saved");
      onSaved?.({ avatar_type: "illustrated", avatar_seed: seed, avatar_config: effectiveConfig });
    } catch { toast.error("Failed to save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="flex flex-col items-center gap-3">
        <div className="w-44 h-44 rounded-full overflow-hidden bg-zinc-100 border" data-testid="avatar-preview">
          <img key={illustratedUrl(previewUser)} src={illustratedUrl(previewUser)} alt="Preview" className="w-full h-full object-cover" />
        </div>
        <div className="flex flex-wrap gap-1 justify-center" data-testid="avatar-expressions">
          {Object.keys(EXPRESSIONS).map((e) => (
            <button key={e} onClick={() => setExpression(e)}
              className={`text-xs rounded-full px-2 py-1 border ${expression === e ? "border-[var(--brand-accent)] bg-orange-50" : "border-zinc-200"}`}
              data-testid={`avatar-expr-${e}`}>{e}</button>
          ))}
        </div>
        <div className="flex gap-1 w-full">
          <Button variant="outline" size="sm" className="flex-1"
            onClick={() => { const c = randomAvatarConfig(); setConfig(c); setSeed(Math.random().toString(36).slice(2, 10)); setExpression("neutral"); }}
            data-testid="avatar-randomize">
            <Sparkles className="w-4 h-4 mr-1" /> Randomize
          </Button>
          <Button variant="outline" size="sm" className="flex-1"
            onClick={() => { setConfig({ ...DEFAULT_AVATAR_CONFIG }); setExpression("neutral"); }}
            data-testid="avatar-reset">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
        </div>
      </div>
      <div className="space-y-3 max-h-[520px] overflow-y-auto scroll-thin pr-1">
        <ColorField label="Skin tone" options={SKIN_TONES} value={config.skinColor} onChange={(v) => set("skinColor", v)} testid="avatar-skin"/>
        <PillField label="Hair style" options={HAIR_STYLES} value={config.top} onChange={(v) => set("top", v)} testid="avatar-hair-style"/>
        <ColorField
          label="Hair color"
          options={HAIR_COLORS}
          value={config.hairColor}
          onChange={(v) => set("hairColor", v)}
          testid="avatar-hair-color"
          disabled={HAIR_HIDDEN_TOPS.has(config.top)}
          disabledHint="Hair color is not visible with this style."
        />
        <PillField
          label="Facial hair"
          options={FACIAL_HAIRS}
          value={config.facialHair || "none"}
          onChange={(v) => set("facialHair", v)}
          testid="avatar-facial-hair"
        />
        <ColorField
          label="Facial hair color"
          options={FACIAL_HAIR_COLORS}
          value={config.facialHairColor || FACIAL_HAIR_COLORS[0].value}
          onChange={(v) => set("facialHairColor", v)}
          testid="avatar-facial-hair-color"
          disabled={!config.facialHair || config.facialHair === "none"}
          disabledHint="Select a facial-hair style to unlock its color."
        />
        <PillField label="Eyes" options={EYES} value={effectiveConfig.eyes} onChange={(v) => { setExpression("neutral"); set("eyes", v); }} testid="avatar-eyes"/>
        <PillField
          label="Eyebrows"
          options={EYEBROWS}
          value={config.eyebrows || "default"}
          onChange={(v) => set("eyebrows", v)}
          testid="avatar-eyebrows"
        />
        <PillField label="Mouth" options={MOUTHS} value={effectiveConfig.mouth} onChange={(v) => { setExpression("neutral"); set("mouth", v); }} testid="avatar-mouth"/>
        <PillField label="Accessories" options={ACCESSORIES} value={config.accessories} onChange={(v) => set("accessories", v)} testid="avatar-accessories"/>
        <PillField label="Clothing" options={CLOTHING} value={config.clothing} onChange={(v) => set("clothing", v)} testid="avatar-clothing"/>
        <ColorField label="Clothing color" options={CLOTHING_COLORS} value={config.clothesColor} onChange={(v) => set("clothesColor", v)} testid="avatar-clothes-color"/>
        <ColorField label="Background" options={BACKGROUND_COLORS} value={config.backgroundColor} onChange={(v) => set("backgroundColor", v)} testid="avatar-bg"/>
        <div className="flex gap-2 pt-3 mt-2 border-t bg-white pb-1">
          <Button variant="outline" onClick={onCancel} data-testid="avatar-illustrated-cancel">Cancel</Button>
          <Button className="flex-1 bg-[var(--brand-primary)] text-white" onClick={save} disabled={busy} data-testid="avatar-save-illustrated">
            {busy ? "Saving…" : "Save avatar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function ColorField({ label, options, value, onChange, testid, disabled = false, disabledHint }) {
  return (
    <Field label={label}>
      <div className={`flex flex-wrap gap-1 ${disabled ? "opacity-40 pointer-events-none" : ""}`} data-testid={testid}>
        {options.map((o) => (
          <button key={o.value} onClick={() => onChange(o.value)} title={o.label} disabled={disabled}
            className={`w-7 h-7 rounded-full border-2 ${value?.toLowerCase() === o.value?.toLowerCase() ? "border-[var(--brand-accent)] scale-110 ring-2 ring-orange-100" : "border-zinc-200"} transition`}
            style={{ backgroundColor: `#${o.value}` }}
            data-testid={`${testid}-${o.value}`}/>
        ))}
      </div>
      {disabled && disabledHint && (
        <div className="text-[11px] text-zinc-500 mt-1" data-testid={`${testid}-disabled-hint`}>{disabledHint}</div>
      )}
    </Field>
  );
}

function PillField({ label, options, value, onChange, testid }) {
  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-1" data-testid={testid}>
        {options.map((o) => (
          <button key={o.value} onClick={() => onChange(o.value)}
            className={`text-xs rounded-full px-2 py-1 border ${value === o.value ? "border-[var(--brand-accent)] bg-orange-50" : "border-zinc-200 hover:border-zinc-300"}`}
            data-testid={`${testid}-${o.value}`}>
            {o.label}
          </button>
        ))}
      </div>
    </Field>
  );
}
